import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck, ArrowLeft, AlertCircle } from "lucide-react";
import { KWLogo } from "@/components/KWLogo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const location = useLocation();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const initialError = location.state?.error;
  const [error, setError] = useState(initialError || "");

  useEffect(() => {
    // If already authenticated, jump straight to /admin
    (async () => {
      try {
        const resp = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (resp.ok) {
          navigate("/admin", { replace: true });
          return;
        }
      } catch (err) {
        console.error("Auth precheck failed:", err?.message || err);
      }
      setChecking(false);
    })();
  }, [navigate]);

  const handleSignIn = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };

  if (checking) return <div className="min-h-screen bg-[color:var(--kw-cream)]" />;

  return (
    <div className="min-h-screen bg-[color:var(--kw-cream)] flex flex-col">
      <div className="kw-red-bar h-1 w-full" />

      <div className="max-w-md w-full mx-auto flex-1 flex flex-col justify-center px-6 py-16">
        <button
          data-testid="back-to-hub"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase text-[color:var(--muted-foreground)] hover:text-[color:var(--kw-charcoal)] mb-10 self-start transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={2.5} />
          Back to Hub
        </button>

        <div className="flex items-center gap-3 mb-8">
          <KWLogo className="h-12" />
          <div className="h-9 w-px bg-[color:var(--kw-border)]" />
          <div className="inline-flex items-center gap-1.5 text-[color:var(--kw-red)]">
            <ShieldCheck size={16} strokeWidth={2.5} />
            <span className="font-black uppercase tracking-widest text-xs">Admin</span>
          </div>
        </div>

        <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight text-[color:var(--kw-charcoal)] leading-[0.95] mb-3">
          Coach Portal
        </h1>
        <p className="text-[color:var(--kw-charcoal-soft)] text-base leading-relaxed mb-10">
          Sign in to review Open House Activity Reports submitted by KW Wellington agents. Access is restricted to authorized coaching staff.
        </p>

        {error && (
          <div
            data-testid="login-error"
            className="mb-6 flex items-start gap-2.5 rounded-xl bg-[#fdecec] border border-[color:var(--kw-red)]/25 px-4 py-3 text-sm text-[color:var(--kw-red-dark)]"
          >
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span className="font-semibold">{error}</span>
          </div>
        )}

        <button
          data-testid="google-signin-btn"
          onClick={handleSignIn}
          className="w-full py-4 rounded-full text-sm kw-btn-primary inline-flex items-center justify-center gap-2.5"
        >
          <GoogleG />
          Sign in with Google
        </button>

        <p className="text-xs text-[color:var(--muted-foreground)] mt-6 leading-relaxed">
          By continuing you agree to KW Wellington&rsquo;s internal usage policies. Your Google email must be on the coaching allowlist.
        </p>
      </div>

      <footer className="border-t border-[color:var(--kw-border)] bg-white py-4 text-center">
        <div className="text-xs text-[color:var(--muted-foreground)]">
          Each Keller Williams Realty office is independently owned and operated.
        </div>
      </footer>
    </div>
  );
}

const GoogleG = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.5 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 20-8 20-20 0-1.3-.1-2.3-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.1 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.3 0 10.2-2 13.9-5.3l-6.4-5.3C29.3 35 26.8 36 24 36c-5.2 0-9.6-3.5-11.2-8.2l-6.5 5C9.6 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.4 5.3C40 34.9 44 30 44 24c0-1.3-.1-2.3-.4-3.5z" />
  </svg>
);
